import React, { useEffect, useId, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Collapse, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { ExpandLess, ExpandMore, Refresh } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { getPulse, planningError } from '../actions/planning';
import PulseEventCards from './PulseEventCards';

export default function PlanningPulse({ eventId, hideWhenEmpty = false, initiallyCollapsed = false, compactHome = false }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [offset, setOffset] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [expanded, setExpanded] = useState(!initiallyCollapsed);
  const detailsId = useId();
  useEffect(() => {
    let active = true;
    setData(null);
    setError('');
    getPulse(eventId, offset).then(result => { if (active) setData(result); })
      .catch(reason => { if (active) setError(planningError(reason)); });
    return () => { active = false; };
  }, [eventId, offset, refresh]);

  if (hideWhenEmpty && data?.total === 0 && !error) return null;
  if (compactHome && data && !error) return <PulseEventCards summaries={data.event_summaries} onRefresh={() => setRefresh(value => value + 1)} />;

  const groups = new Map();
  for (const item of data?.items || []) {
    const key = eventId || item.event_id;
    if (!groups.has(key)) groups.set(key, { title: item.event_title, items: [] });
    groups.get(key).items.push(item);
  }

  return (
    <Paper component="section" aria-label="Planning Pulse" variant="outlined" sx={{ p: 2, borderRadius: 3, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2}>
        <Box>
          <Typography variant="h6" fontWeight={700}>Planning Pulse</Typography>
          <Typography color="text.secondary" variant="body2">{data?.total > 0 ? `${data.total} item${data.total === 1 ? '' : 's'} need${data.total === 1 ? 's' : ''} attention` : 'Your next steps, all in one place.'}</Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
        <Tooltip title="Refresh" arrow>
          <span>
            <IconButton aria-label="Refresh" color="primary" onClick={() => setRefresh(value => value + 1)} disabled={!data && !error}>
              <Refresh />
            </IconButton>
          </span>
        </Tooltip>
        {initiallyCollapsed && data?.total > 0 && <Tooltip title={expanded ? 'Hide details' : 'Show details'} arrow>
          <IconButton aria-label={expanded ? 'Hide details' : 'Show details'} aria-expanded={expanded} aria-controls={detailsId} onClick={() => setExpanded(value => !value)}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Tooltip>}
        </Stack>
      </Stack>
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      {!data && !error && <CircularProgress size={24} aria-label="Loading Planning Pulse" sx={{ mt: 2 }} />}
      {data && <>
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ mt: 1.5, mb: expanded ? 1 : 0 }}>
          {Object.entries(data.counts).filter(([key]) => key !== 'budget').map(([key, count]) =>
            <Chip key={key} label={`${{ task: 'Tasks', rsvp: 'RSVP follow-ups', payment: 'Payments', date_review: 'Date reviews' }[key] || key}: ${count}`} size="small" />)}
          {Object.entries(data.budget_checks || {}).filter(([check]) => !expanded || !data.items.some(item => item.kind === 'budget' && item.title === check)).map(([check, count]) =>
            <Chip key={check} label={`${check}${count > 1 ? ` (${count} events)` : ''}`} size="small" color="warning" sx={{ height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5 } }} />)}
        </Stack>
        {data.total === 0 && <Typography color="text.secondary">You are all caught up. Add tasks, guests, or expenses to bring your plan to life.</Typography>}
        <Collapse in={expanded} id={detailsId}>
        <Stack spacing={eventId ? 0 : 2}>
          {Array.from(groups, ([id, group]) => <Box key={id} component={eventId ? 'div' : 'section'} aria-label={eventId ? undefined : `${group.title} attention items`}>
            {!eventId && <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ py: 1, borderBottom: 1, borderColor: 'divider' }}>
              <Typography component="h3" variant="subtitle1" fontWeight={700} sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>{group.title}</Typography>
              <Chip size="small" label={`${group.items.length} item${group.items.length === 1 ? '' : 's'}${data.total > 20 ? ' on this page' : ''}`} sx={{ flexShrink: 0 }} />
            </Stack>}
          {group.items.map((item, index) =>
            <Box key={`${item.event_id}-${index}`} sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', py: 1, minWidth: 0 }}>
              <Box sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                <Typography fontWeight={600} color={item.severity === 'error' ? 'error.main' : 'text.primary'}>{item.title}</Typography>
              </Box>
              <Button component={Link} to={item.href} size="small" sx={{ flexShrink: 0 }}>Review</Button>
            </Box>)}
          </Box>)}
        </Stack>
        {data.total > 20 && <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 2 }}>
          <Button disabled={offset === 0} onClick={() => setOffset(value => Math.max(0, value - 20))}>Previous</Button>
          <Typography variant="caption">{offset + 1}-{Math.min(offset + 20, data.total)} of {data.total}</Typography>
          <Button disabled={offset + 20 >= data.total} onClick={() => setOffset(value => value + 20)}>Next</Button>
        </Stack>}
        </Collapse>
      </>}
    </Paper>
  );
}
